/* ConfigAuditParamsRenderer.js */

"use strict";

/**
 * @type {{Control: {
 *  Action: {Button: ButtonControlComponent},
 *  Textfield: {InputText: InputTextControlComponent},
 *  Boolean: {InputCheckbox: InputCheckboxControlComponent}
 *  OptionSelection: {
 *      Single: {InputRadio: InputRadioControlComponent, ComboBox: ComboBoxControlComponent}
 *  }
 * }}}
 */
const uiCore = require("@gluenetworks/steve_ui_core");

const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;

class ConfigAuditActionParamsRenderer {
    constructor(model, data, logger) {
        this._model = model;
        this._logger = logger;
        this._rootElem = document.createElement("div");
        this._rootElem.classList.add("config-audit-screen-params");
        this._rootElem.component = this._model;

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Audit Configuration";

        this._auditTitleInput = new InputTextControl({
            darkTheme: true,
            value: data.name ? data.name : "Audit Run - " + new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString()
        });

        this._devicesContainer = document.createElement("div");
        this._devicesContainer.classList.add("devices-container");
        this._devicesLabel = document.createElement("label");
        this._devicesLabel.innerHTML = "Devices:";
        this._devicesSelection = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
            options: [
                {
                    label: "Selection",
                    value: "selection"
                },
                {
                    label: "Apply Current Filter",
                    value: "apply_current_filter"
                },
                {
                    label: "All",
                    value: "all"
                }
            ],
            value: data.selection
        });

        this._captureNewSnapshotContainer = document.createElement("div");
        this._captureNewSnapshotContainer.classList.add("capture-snapshort-container");
        this._captureNewSnapshot = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Capture New Snapshot",
            value: data.captureName !== undefined
        });

        this._captureNewSnapshotInput = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter a name for this Snapshot (optional)",
            value: data.captureName || ""
        });

        this._auditPolicyContainer = document.createElement("div");
        this._auditPolicyContainer.classList.add("audit-policy-container");

        this._auditPolicyLabel = document.createElement("label");
        this._auditPolicyLabel.innerHTML = "Policy: ";
        this._auditPolicy = new ComboBoxControl({ options: [], showFilter: true });

        this._buttonBarElem = document.createElement("div");
        this._buttonBarElem.classList.add("button-bar");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true
        });

        this._scheduleAuditBtn = new ButtonControl({
            label: "Schedule Audit",
            darkTheme: true
        });

        this._startAuditBtn = new ButtonControl({
            label: "Start Audit",
            darkTheme: true
        });

        this.setPolicyLabels = this.setPolicyLabels.bind(this);
        this._onInputChange = this._onInputChange.bind(this);

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this.model.on("PolicyLabels", this.setPolicyLabels);

        this._auditTitleInput.addListener("UserValue", this._onInputChange);
        this._captureNewSnapshot.addListener("UserValue", this._onInputChange);
        this._captureNewSnapshotInput.addListener("UserValue", this._onInputChange);
        this._auditPolicy.addListener("UserValue", this._onInputChange);

        this._scheduleAuditBtn.on("UserClick", this._onScheduleClick.bind(this));
        this._startAuditBtn.on("UserClick", this._onStartClick.bind(this));
        this._cancelButton.on("UserClick", (this._onCancelButtonClick = this._onCancelButtonClick.bind(this)));
    }

    _unregisterListeners() {
        this.model.removeListener("PolicyLabels", this.setPolicyLabels);

        this._auditTitleInput.removeListener("UserValue", this._onInputChange);
        this._captureNewSnapshot.removeListener("UserValue", this._onInputChange);
        this._captureNewSnapshotInput.removeListener("UserValue", this._onInputChange);
        this._auditPolicy.removeListener("UserValue", this._onInputChange);

        this._scheduleAuditBtn.removeListener("UserClick", this._onScheduleClick);
        this._startAuditBtn.removeListener("UserClick", this._onStartClick);
        this._cancelButton.removeAllListeners("UserClick");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        if (this._rootElem === null) {
            return Promise.resolve();
        }

        this.shutdownPromise =
            this.shutdownPromise ||
            Promise.resolve()
                .then(() => {
                    this._unregisterListeners();
                    this._rootElem.remove();
                })
                .then(() =>
                    Promise.all([
                        this._auditTitleInput.shutdown(),
                        this._devicesSelection.shutdown(),
                        this._captureNewSnapshot.shutdown(),
                        this._captureNewSnapshotInput.shutdown(),
                        this._auditPolicy.shutdown(),
                        this._scheduleAuditBtn.shutdown(),
                        this._startAuditBtn.shutdown(),
                        this._cancelButton.shutdown()
                    ])
                )
                .then(() => {
                    this._auditTitleInput = null;
                    this._devicesSelection = null;
                    this._captureNewSnapshot = null;
                    this._captureNewSnapshotInput = null;
                    this._auditPolicy = null;
                    this._scheduleAuditBtn = null;
                    this._startAuditBtn = null;
                    this._cancelButton = undefined;

                    this._model = null;
                    this.shutdownPromise = null;
                    this._logger = null;
                    this._rootElem = null;
                })
                .catch((error) => this._logger.warn({ error }, `${this.constructor.name}  shutdown Error`));

        return this.shutdownPromise;
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._checkButtonsAndInputs();

        return Promise.resolve().then(() => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleText);
                this._rootElem.appendChild(this._auditTitleInput.renderer.rootElement);

                if (this._devicesSelection.getValue()) {
                    this._devicesContainer.appendChild(this._devicesLabel);
                    this._devicesContainer.appendChild(this._devicesSelection.renderer.rootElement);
                    this._rootElem.appendChild(this._devicesContainer);
                }

                this._captureNewSnapshotContainer.appendChild(this._captureNewSnapshot.renderer.rootElement);
                this._captureNewSnapshotContainer.appendChild(this._captureNewSnapshotInput.renderer.rootElement);
                this._rootElem.appendChild(this._captureNewSnapshotContainer);

                this._auditPolicyContainer.appendChild(this._auditPolicyLabel);
                this._auditPolicyContainer.appendChild(this._auditPolicy.renderer.rootElement);
                this._rootElem.appendChild(this._auditPolicyContainer);

                this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonBarElem.appendChild(this._scheduleAuditBtn.renderer.rootElement);
                this._buttonBarElem.appendChild(this._startAuditBtn.renderer.rootElement);
                this._rootElem.appendChild(this._buttonBarElem);
            }
        });
    }

    get renderer() {
        return this;
    }

    getRootElement() {
        return this._rootElem;
    }
    get rootElement() {
        return this.getRootElement();
    }

    getModel() {
        return this._model;
    }
    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    setPolicyLabels(labelValues) {
        this._auditPolicy.setOptions(labelValues);
    }

    /**
     * resolve this view and shutdown;
     * @private
     */
    _onStartClick() {
        this.model.configAuditStart(this._getData());
    }

    /**
     * show schedule view
     * @private
     */
    _onScheduleClick() {
        this.model.configAuditSchedule(this._getData());
    }

    _onInputChange() {
        this._checkButtonsAndInputs();
    }

    _onCancelButtonClick() {
        let err = new Error("user rejected");
        err.userRejected = true;
        this.model.cancelAction(err);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _getData() {
        const captureNewSnapShots = this._captureNewSnapshot.getValue();
        const snapshotName = this._captureNewSnapshotInput.getValue();
        const policyId = this._auditPolicy.getValue();
        const policy = policyId ? this._auditPolicy.options.find(({ value }) => value === policyId) : null;

        const data = {
            auditTitle: this._auditTitleInput.getValue(),
            selection: this._devicesSelection.getValue(),
            captureNewSnapShots,
            snapshotName,
            policy: {
                id: policyId,
                name: policy && policy.label
            }
        };
        return data;
    }

    _checkButtonsAndInputs() {
        const policyId = this._auditPolicy.getValue();
        const captureNewSnapshot = this._captureNewSnapshot.getValue();

        if (policyId) {
            this._enableViewActions();
        } else {
            this._disableViewActions();
        }

        this._captureNewSnapshotInput.setDisabled(!captureNewSnapshot);
    }

    _disableViewActions() {
        this._scheduleAuditBtn.setDisabled(true);
        this._startAuditBtn.setDisabled(true);
    }

    _enableViewActions() {
        this._scheduleAuditBtn.setDisabled(false);
        this._startAuditBtn.setDisabled(false);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = ConfigAuditActionParamsRenderer;
